import api, { handleResponse, handleError } from './api';

/**
 * In-app notices — the conditional resume prompt and admin announcements that
 * render as a modal (desktop) or bottom sheet (mobile).
 * NOT the email newsletter system (services/newsletterService).
 * docs/plans/2026-08-handoff-layout/09-app-notices.md
 */
const appNoticeService = {
  /**
   * Returns `{ interrupting, banner }` — the modal/sheet and the persistent
   * resume banner are independent, so a banner never starves an announcement.
   * `context` names a moment we must never interrupt: checkout | signal | first_session.
   */
  async next(context = null, lat = null, lng = null) {
    try {
      const q = new URLSearchParams();
      if (context) q.set('context', context);
      // Geofenced notices need to know where the person is; the tracker's last
      // location is good enough (a city-scale radius, never an address).
      if (Number.isFinite(lat) && Number.isFinite(lng)) { q.set('lat', lat); q.set('lng', lng); }
      const qs = q.toString();
      return handleResponse(await api.get(`/app-notices/next${qs ? `?${qs}` : ''}`));
    } catch (e) { return handleError(e); }
  },

  async act(id) {
    try {
      const r = await api.post(`/app-notices/${id}/act`);
      return { success: r.status >= 200 && r.status < 300, data: null, error: null };
    } catch (e) { return handleError(e); }
  },

  async dismiss(id) {
    try {
      const r = await api.post(`/app-notices/${id}/dismiss`);
      return { success: r.status >= 200 && r.status < 300, data: null, error: null };
    } catch (e) { return handleError(e); }
  },

  /** Quiet the persistent banner for a day (no strike — they're already downgraded). */
  async hideResumeBanner() {
    try {
      const r = await api.post('/app-notices/resume-banner/hide');
      return { success: r.status >= 200 && r.status < 300, data: null, error: null };
    } catch (e) { return handleError(e); }
  },

  async dismissResumePrompt() {
    try {
      const r = await api.post('/app-notices/resume-prompt/dismiss');
      return { success: r.status >= 200 && r.status < 300, data: null, error: null };
    } catch (e) { return handleError(e); }
  },

  // ── admin composer ──────────────────────────────────────────────────────
  async list() {
    try { return handleResponse(await api.get('/app-notices/admin')); }
    catch (e) { return handleError(e); }
  },
  async create(payload) {
    try { return handleResponse(await api.post('/app-notices/admin', payload)); }
    catch (e) { return handleError(e); }
  },
  async update(id, payload) {
    try { return handleResponse(await api.put(`/app-notices/admin/${id}`, payload)); }
    catch (e) { return handleError(e); }
  },
  async remove(id) {
    try {
      const r = await api.delete(`/app-notices/admin/${id}`);
      return { success: r.status >= 200 && r.status < 300, data: null, error: null };
    } catch (e) { return handleError(e); }
  },

  // ── audiences (shared by sheets and pushes) ─────────────────────────────
  /** Hero / push image → `{ path, url }`. Same WebP pipeline as posters, 1200px wide. */
  async uploadImage(file) {
    try {
      const form = new FormData();
      form.append('file', file);
      return handleResponse(await api.post('/app-notices/admin/image', form, { headers: { 'Content-Type': 'multipart/form-data' } }));
    } catch (e) { return handleError(e); }
  },
  /** How many people an audience + geofence reaches right now → `{ count }`. */
  async audienceCount(audience, cities) {
    try { return handleResponse(await api.post('/app-notices/admin/audience/count', { audience, cities })); }
    catch (e) { return handleError(e); }
  },
  async people(q = '') {
    try { return handleResponse(await api.get(`/app-notices/admin/people?q=${encodeURIComponent(q)}`)); }
    catch (e) { return handleError(e); }
  },
  async clubs(q = '') {
    try { return handleResponse(await api.get(`/app-notices/admin/clubs${q ? `?q=${encodeURIComponent(q)}` : ''}`)); }
    catch (e) { return handleError(e); }
  },

  // ── the link picker (GOES TO / OPENS ON TAP) ────────────────────────────
  /** Events, clubs, Pro pages, people and cities for the picker → `[{ kind, path, label, sub, img }]`. */
  async links(q = '') {
    try { return handleResponse(await api.get(`/app-notices/admin/links${q ? `?q=${encodeURIComponent(q)}` : ''}`)); }
    catch (e) { return handleError(e); }
  },
  /** The server's answer for a path the rules alone cannot settle → `{ kind, label, note, ok, warn, path, image }`. */
  async resolveLink(path, mode = 'sheet') {
    try { return handleResponse(await api.get(`/app-notices/admin/links/resolve?path=${encodeURIComponent(path)}&mode=${mode}`)); }
    catch (e) { return handleError(e); }
  },

  // ── custom pushes ───────────────────────────────────────────────────────
  async pushes() {
    try { return handleResponse(await api.get('/app-notices/admin/pushes')); }
    catch (e) { return handleError(e); }
  },
  async createPush(payload) {
    try { return handleResponse(await api.post('/app-notices/admin/pushes', payload)); }
    catch (e) { return handleError(e); }
  },
  async updatePush(id, payload) {
    try { return handleResponse(await api.put(`/app-notices/admin/pushes/${id}`, payload)); }
    catch (e) { return handleError(e); }
  },
  async removePush(id) {
    try {
      const r = await api.delete(`/app-notices/admin/pushes/${id}`);
      return { success: r.status >= 200 && r.status < 300, data: null, error: null };
    } catch (e) { return handleError(e); }
  },
  /** Superadmin only. Resolves the audience and sends; the record freezes. */
  async sendPush(id) {
    try { return handleResponse(await api.post(`/app-notices/admin/pushes/${id}/send`)); }
    catch (e) { return handleError(e); }
  },
};

export default appNoticeService;
